import React from 'react';
import { FeedbackState } from '../types';
import clsx from 'clsx';

/**
 * Props for the ChallengeInput component
 * @typedef {Object} ChallengeInputProps
 * @property {string} userAnswer - Current user's answer
 * @property {(answer: string) => void} setUserAnswer - Function to update user's answer
 * @property {boolean} isLoading - Loading state
 * @property {FeedbackState} feedback - Current feedback state
 * @property {number} fontSize - Font size in pixels
 * @property {string} fontFamily - Font family for code input
 * @property {() => void} onSubmit - Submit handler
 * @property {() => void} onNext - Next challenge handler
 */
interface ChallengeInputProps {
  userAnswer: string;
  setUserAnswer: (answer: string) => void;
  isLoading: boolean;
  feedback: FeedbackState;
  fontSize: number;
  fontFamily: string;
  onSubmit: () => void;
  onNext: () => void;
}

/**
 * ChallengeInput component for handling user code input with DaisyUI styling
 * @param {ChallengeInputProps} props - Component properties
 * @returns {JSX.Element} Rendered component
 */
const ChallengeInput: React.FC<ChallengeInputProps> = ({
  userAnswer,
  setUserAnswer,
  isLoading,
  feedback,
  fontSize,
  fontFamily,
  onSubmit,
  onNext
}) => {
  /**
   * Handles button click based on feedback state
   * @param {React.MouseEvent} e - Click event
   */
  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (feedback.isVisible) {
      onNext();
    } else {
      onSubmit();
    }
  };

  return (
    <div className="space-y-4">
      <textarea
        value={userAnswer}
        onChange={(e) => setUserAnswer(e.target.value)}
        placeholder="Digite sua resposta aqui..."
        style={{ fontSize: `${fontSize}px`, fontFamily }}
        disabled={feedback.isVisible}
        className={clsx(
          'textarea w-full min-h-[100px]',
          'bg-base-300/90 text-base-content',
          'border-2 border-base-content/20',
          'focus:outline-none focus:border-info',
          'placeholder:text-base-content/50',
          'font-mono',
          {
            'border-success/50 bg-success/10 text-success': feedback.isVisible,
            'textarea-disabled opacity-70': feedback.isVisible
          }
        )}
      />
      
      <div className="flex justify-end">
        <button
          onClick={handleButtonClick}
          disabled={isLoading || (!feedback.isVisible && !userAnswer.trim())}
          className={clsx(
            'btn gap-2',
            {
              'btn-info': !feedback.isVisible,
              'btn-success': feedback.isVisible,
              'opacity-50 cursor-not-allowed': isLoading || (!feedback.isVisible && !userAnswer.trim()),
              'loading': isLoading
            }
          )}
        >
          {isLoading ? 'Carregando...' : feedback.isVisible ? 'Próximo Desafio' : 'Enviar Resposta'}
        </button>
      </div>
    </div>
  );
};

export default ChallengeInput;
